import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AuditRow, Page } from '@elutide/shared';
import { api, ApiProblem } from '@/lib/api';
import { qk } from '@/lib/keys';
import { AgeText } from '@/components/Provenance';
import {
  Button,
  Card,
  Chip,
  DataTable,
  Drawer,
  EmptyState,
  ErrorState,
  Pill,
  TextInput,
  type Column,
} from '@/components';

type Preset = 'all' | 'money' | 'controls' | 'auth' | 'pii';

/**
 * `action` is matched by the API as a prefix (`action LIKE $1 || '%'`), so each value here
 * has to be a real prefix of an action the API actually writes. The originals did not
 * match anything: `control.` (the action is `settings.controls.<id>`) and
 * `customers.reveal_contact` (the action is `customers.pii.reveal`) both returned an empty
 * table that looked like a quiet log rather than a broken filter.
 */
const PRESETS: readonly { id: Preset; label: string; action?: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'money', label: 'Money', action: 'orders.refund' },
  { id: 'controls', label: 'Controls', action: 'settings.controls' },
  { id: 'auth', label: 'Auth', action: 'auth.' },
  { id: 'pii', label: 'PII reveals', action: 'customers.pii.reveal' },
];

/**
 * Settings > Audit. The product's memory: who, what, before and after, reason, device and
 * request id, hash chained to the row before it.
 *
 * The application database user holds INSERT and SELECT on this table and nothing else, so
 * nothing on this screen can edit what it shows.
 */
export function AuditLog() {
  const [preset, setPreset] = useState<Preset>('all');
  const [actor, setActor] = useState('');
  const [objectId, setObjectId] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [selected, setSelected] = useState<AuditRow | null>(null);

  const action = PRESETS.find((entry) => entry.id === preset)?.action;
  const filters = {
    ...(action ? { action } : {}),
    ...(actor ? { actor } : {}),
    ...(objectId ? { object_id: objectId } : {}),
    limit: 50,
    ...(cursor ? { cursor } : {}),
  };

  const query = useQuery({
    queryKey: qk.audit(filters, cursor),
    queryFn: () => api.get<Page<AuditRow>>('/audit', { query: filters }),
  });

  const columns: readonly Column<AuditRow>[] = [
    { id: 'ts', header: 'When', priority: 1, render: (row) => <AgeText at={row.ts} /> },
    {
      id: 'actor',
      header: 'Who',
      priority: 1,
      render: (row) => (
        <span>
          {row.actor_system ?? row.actor_user_id ?? 'unknown'}{' '}
          <span className="o-muted">{row.actor_role ?? ''}</span>
        </span>
      ),
    },
    { id: 'action', header: 'Action', priority: 1, mono: true, render: (row) => <span>{row.action}</span> },
    {
      id: 'object',
      header: 'Object',
      priority: 2,
      mono: true,
      render: (row) => <span>{row.object_type ? `${row.object_type} ${row.object_id ?? ''}` : 'none'}</span>,
    },
    {
      id: 'result',
      header: 'Result',
      priority: 2,
      render: (row) => (
        <Pill tone={row.result === 'success' ? 'ok' : row.result === 'denied' ? 'crit' : 'warn'}>
          {row.result}
        </Pill>
      ),
    },
    {
      id: 'tier',
      header: 'Tier',
      priority: 3,
      align: 'right',
      mono: true,
      render: (row) => <span>{row.tier}</span>,
    },
    {
      id: 'reason',
      header: 'Reason',
      priority: 4,
      render: (row) => <span>{row.reason ?? 'none'}</span>,
    },
    {
      id: 'request',
      header: 'Request',
      priority: 5,
      mono: true,
      render: (row) => <span>{row.request_id ? row.request_id.slice(-6) : 'none'}</span>,
    },
  ];

  return (
    <>
      <Card title="Audit log" id="audit">
        <div className="o-row" role="group" aria-label="Audit filters">
          {PRESETS.map((entry) => (
            <Chip
              key={entry.id}
              label={entry.label}
              active={preset === entry.id}
              onClick={() => {
                setPreset(entry.id);
                setCursor(null);
              }}
            />
          ))}
        </div>

        <div className="o-row">
          <TextInput
            label="Actor"
            value={actor}
            onChange={(event) => {
              setActor(event.target.value);
              setCursor(null);
            }}
            hint="User id. The server matches it exactly, so a handle finds nothing."
          />
          <TextInput
            label="Object id"
            mono
            value={objectId}
            onChange={(event) => {
              setObjectId(event.target.value);
              setCursor(null);
            }}
            hint="Order number, customer key, SKU."
          />
        </div>

        {query.isError ? (
          <ErrorState
            message={
              query.error instanceof ApiProblem ? query.error.message : 'The audit log did not load.'
            }
            requestId={query.error instanceof ApiProblem ? query.error.shortRequestId : null}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <DataTable<AuditRow>
            caption="Audit rows"
            columns={columns}
            rows={query.data?.items ?? []}
            rowKey={(row) => row.audit_id}
            loading={query.isLoading}
            onRowClick={(row) => setSelected(row)}
            emptyState={<EmptyState message="No audit rows match these filters." />}
          />
        )}

        {query.data?.next_cursor ? (
          <Button variant="ghost" onClick={() => setCursor(query.data?.next_cursor ?? null)}>
            Load more
          </Button>
        ) : null}
      </Card>

      <Drawer
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected ? selected.action : ''}
        id="audit-drawer"
      >
        {selected ? (
          <div className="o-stack">
            <div className="o-kv">
              <span className="o-kv__key">When</span>
              <span className="o-kv__value">
                <AgeText at={selected.ts} />
              </span>
            </div>
            <div className="o-kv">
              <span className="o-kv__key">Request id</span>
              <span className="o-kv__value">{selected.request_id ?? 'none'}</span>
            </div>
            <div className="o-kv">
              <span className="o-kv__key">Device</span>
              <span className="o-kv__value">{selected.device_id ?? 'none'}</span>
            </div>
            <div className="o-kv">
              <span className="o-kv__key">IP</span>
              <span className="o-kv__value">{selected.ip ?? 'none'}</span>
            </div>
            <h3 className="o-section-title">Before</h3>
            <pre className="o-mono o-muted">{JSON.stringify(selected.before, null, 2)}</pre>
            <h3 className="o-section-title">After</h3>
            <pre className="o-mono o-muted">{JSON.stringify(selected.after, null, 2)}</pre>
          </div>
        ) : null}
      </Drawer>
    </>
  );
}
